import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate, fmtINR } from "@/lib/api";
import { PageHeader, DataTable } from "@/components/DataViews";
import StatusBadge from "@/components/StatusBadge";
import ExportButton from "@/components/ExportButton";
import { useAuth } from "@/lib/auth";

function DeliveryBar({ pct }) {
  const clamp = Math.max(0, Math.min(100, Number(pct || 0)));
  const color = clamp >= 100 ? "bg-emerald-500" : clamp > 50 ? "bg-kabir" : clamp > 0 ? "bg-blue-500" : "bg-zinc-300 dark:bg-zinc-700";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${clamp}%` }} />
      </div>
      <span className="text-[11px] font-mono tabular-nums w-10 text-right text-muted-foreground">{clamp.toFixed(0)}%</span>
    </div>
  );
}

export default function POsList() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const { fieldHidden } = useAuth();
  useEffect(() => { api.get("/pos").then((r) => setItems(r.data)); }, []);

  const columns = [
    { key: "po_number", label: "PO Number", render: (r) => <span className="font-mono text-xs font-semibold">{r.po_number || r.draft_po_id}</span> },
    { key: "vendor_name", label: "Vendor" },
    { key: "project_name", label: "Project" },
    { key: "items", label: "Items", render: (r) => r.items?.length || 0 },
    ...(fieldHidden("po", "grand_total") ? [] : [{ key: "grand_total", label: "Value", render: (r) => <span className="font-mono">{fmtINR(r.grand_total)}</span> }]),
    { key: "delivered_pct", label: "Delivery", render: (r) => <DeliveryBar pct={r.delivered_pct} /> },
    { key: "grn_count", label: "GRNs", render: (r) => <span className="font-mono text-xs">{r.grn_count || 0}</span> },
    { key: "created_at", label: "Date", render: (r) => fmtDate(r.approved_at || r.created_at) },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div data-testid="pos-page">
      <PageHeader
        title="Purchase Orders"
        subtitle="POs are active immediately on creation. Track delivery progress against each order."
        actions={<ExportButton entity="pos" filename="purchase-orders.xlsx" />}
      />
      <DataTable
        testid="pos-table"
        columns={columns}
        data={items}
        empty="No purchase orders yet. Freeze a requirement and click 'Create Purchase Order'."
        onRow={(r) => nav(`/pos/${r.id}`)}
      />
    </div>
  );
}
